import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorHandlingService } from '../general/error-handling.service';
import {
  IPathwayEnrollmentRequest,
  IPathwayEnrollmentResponse,
  IPathwayNextStepResponse,
  IPathwayRunStepResponse,
  IPathwayCompleteStepResponse,
  IRunDebriefResponse,
  ICompleteDebriefRequest,
  ICompleteDebriefResponse,
  IPathwayExitStepResponse,
} from 'src/app/interfaces/ipathway-enrollment';
import { IPathwayDetailResponse } from 'src/app/interfaces/ipathway-details-response';
import { IPdfLearnState } from 'src/app/interfaces/ipdf-learn-state';
import { IVideoLearnState } from 'src/app/interfaces/ivideo-learn-state';
import { IScormLearnState } from 'src/app/interfaces/iscorm-learn-state';
import { sendKeepalivePost } from 'src/app/utils/keepalive-request.util';

@Injectable({
  providedIn: 'root',
})
export class PathwaysService {
  backendUrl: string = environment.backendUrl;
  // Each pathway's step languages by enrollment step, refreshed whenever its enrollment loads.
  private readonly stepLanguages = new Map<string, Map<string, string>>();

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) {}

  getorCreatePathwayEnrollment(
    moduleAssignmentUuid: string
  ): Observable<IPathwayEnrollmentResponse> {
    if (!moduleAssignmentUuid) {
      throw new Error('moduleAssignmentUuid is required');
    }

    const requestBody: IPathwayEnrollmentRequest = {
      moduleAssignmentUuid,
    };

    return this.http
      .post<IPathwayEnrollmentResponse>(
        `${this.backendUrl}/pathwayLearner/getEnrollment`,
        requestBody
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getorCreatePathwayEnrollment: ${outcome}`);
        }),
        tap((enrollment) => this.saveStepLanguages(moduleAssignmentUuid, enrollment)),
        catchError(
          this.errorHandlingService.handleError<IPathwayEnrollmentResponse>(
            'getorCreatePathwayEnrollment request'
          )
        )
      );
  }

  // The call language of a pathway step; '' when the step has none or the enrollment can't load.
  getStepLanguage(moduleAssignmentUuid: string, enrollmentStepUuid: string): Observable<string> {
    const cached = this.stepLanguages.get(moduleAssignmentUuid);
    if (cached?.has(enrollmentStepUuid)) {
      return of(cached.get(enrollmentStepUuid) ?? '');
    }
    return this.getorCreatePathwayEnrollment(moduleAssignmentUuid).pipe(
      map(() => this.stepLanguages.get(moduleAssignmentUuid)?.get(enrollmentStepUuid) ?? ''),
      catchError(() => of(''))
    );
  }

  private saveStepLanguages(moduleAssignmentUuid: string, enrollment: IPathwayEnrollmentResponse | null): void {
    if (!enrollment) return;
    const languages = new Map(
      (enrollment.focusAreaGroups ?? [])
        .flatMap((group) => group.steps)
        .map((step): [string, string] => [step.enrollmentStepUuid ?? '', step.language ?? ''])
    );
    this.stepLanguages.set(moduleAssignmentUuid, languages);
  }

  createEnrollment(
    moduleAssignmentUuid: string
  ): Observable<IPathwayEnrollmentResponse> {
    if (!moduleAssignmentUuid) {
      throw new Error('moduleAssignmentUuid is required');
    }

    const requestBody: IPathwayEnrollmentRequest = {
      moduleAssignmentUuid,
    };

    return this.http
      .post<IPathwayEnrollmentResponse>(
        `${this.backendUrl}/pathwayLearner/createEnrollment`,
        requestBody
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'created' : 'did not create';
          console.log(`createEnrollment: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayEnrollmentResponse>(
            'createEnrollment request'
          )
        )
      );
  }

  getNextStepByEnrollmentUuid(
    enrollmentUuid: string
  ): Observable<IPathwayNextStepResponse> {
    if (!enrollmentUuid) {
      throw new Error('enrollmentUuid is required');
    }

    const params = new HttpParams().set('enrollmentUuid', enrollmentUuid);

    return this.http
      .get<IPathwayNextStepResponse>(
        `${this.backendUrl}/pathwayLearner/getNextStepByEnrollmentUuid`,
        { params }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getNextStepByEnrollmentUuid: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayNextStepResponse>(
            'getNextStepByEnrollmentUuid request'
          )
        )
      );
  }

  runStepByEnrollmentUuid(
    enrollmentUuid: string,
    stepUuid: string
  ): Observable<IPathwayRunStepResponse> {
    if (!enrollmentUuid) {
      throw new Error('enrollmentUuid is required');
    }
    if (!stepUuid) {
      throw new Error('stepUuid is required');
    }

    const params = new HttpParams()
      .set('enrollmentUuid', enrollmentUuid)
      .set('stepUuid', stepUuid);

    return this.http
      .post<IPathwayRunStepResponse>(
        `${this.backendUrl}/pathwayLearner/runStepByEnrollmentUuid`,
        null,
        { params }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'executed' : 'did not execute';
          console.log(`runStepByEnrollmentUuid: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayRunStepResponse>(
            'runStepByEnrollmentUuid request'
          )
        )
      );
  }

  completeStepByEnrollmentUuid(
    enrollmentUuid: string,
    stepUuid: string
  ): Observable<IPathwayCompleteStepResponse> {
    if (!enrollmentUuid) {
      throw new Error('enrollmentUuid is required');
    }
    if (!stepUuid) {
      throw new Error('stepUuid is required');
    }

    const params = new HttpParams()
      .set('enrollmentUuid', enrollmentUuid)
      .set('stepUuid', stepUuid);

    return this.http
      .post<IPathwayCompleteStepResponse>(
        `${this.backendUrl}/pathwayLearner/completeStepByEnrollmentUuid`,
        null,
        { params }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'completed' : 'did not complete';
          console.log(`completeStepByEnrollmentUuid: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayCompleteStepResponse>(
            'completeStepByEnrollmentUuid request'
          )
        )
      );
  }

  exitStepByEnrollmentUuid(
    enrollmentUuid: string,
    stepUuid: string
  ): Observable<IPathwayExitStepResponse> {
    if (!enrollmentUuid) {
      throw new Error('enrollmentUuid is required');
    }
    if (!stepUuid) {
      throw new Error('stepUuid is required');
    }

    const params = new HttpParams()
      .set('enrollmentUuid', enrollmentUuid)
      .set('stepUuid', stepUuid);

    return this.http
      .post<IPathwayExitStepResponse>(
        `${this.backendUrl}/pathwayLearner/exitStepByEnrollmentUuid`,
        null,
        { params }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'exited' : 'did not exit';
          console.log(`exitStepByEnrollmentUuid: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayExitStepResponse>(
            'exitStepByEnrollmentUuid request'
          )
        )
      );
  }

  /** Same as exitStepByEnrollmentUuid, but sent once and survives the page closing. */
  exitStepByEnrollmentUuidKeepalive(enrollmentUuid: string, stepUuid: string): Promise<IPathwayExitStepResponse | null> {
    const params = new HttpParams()
      .set('enrollmentUuid', enrollmentUuid)
      .set('stepUuid', stepUuid);
    return sendKeepalivePost(`${this.backendUrl}/pathwayLearner/exitStepByEnrollmentUuid?${params.toString()}`)
      .then((res) => (res ? (res.json() as Promise<IPathwayExitStepResponse>) : null))
      .catch(() => null);
  }

  getPathwayDetails(
    moduleAssignmentUuid: string,
    includeStepInfo: boolean
  ): Observable<IPathwayDetailResponse> {
    if (!moduleAssignmentUuid) {
      throw new Error('moduleAssignmentUuid is required');
    }

    if (includeStepInfo === null || includeStepInfo === undefined) {
      throw new Error('includeStepInfo is required');
    }

    const params = new HttpParams()
      .set('moduleAssignmentUuid', moduleAssignmentUuid)
      .set('includeStepInfo', includeStepInfo.toString());

    return this.http
      .get<IPathwayDetailResponse>(
        `${this.backendUrl}/pathway/getPathwayDetails`,
        { params }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'executed' : 'did not execute';
          console.log(`getPathwayDetails: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayDetailResponse>(
            'getPathwayDetails request'
          )
        )
      );
  }

  runDebrief(enrollmentUuid: string, stepUuid: string): Observable<IRunDebriefResponse> {
    const params = new HttpParams()
      .set('enrollmentUuid', enrollmentUuid)
      .set('stepUuid', stepUuid);

    return this.http
      .post<IRunDebriefResponse>(
        `${this.backendUrl}/pathwayLearner/runDebrief`,
        null,
        { params },
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'started' : 'did not start';
          console.log(`runDebrief: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IRunDebriefResponse>(
            'runDebrief request',
          ),
        ),
      );
  }

  completeDebrief(request: ICompleteDebriefRequest): Observable<ICompleteDebriefResponse> {
    return this.http
      .post<ICompleteDebriefResponse>(
        `${this.backendUrl}/pathwayLearner/completeDebrief`,
        request,
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'completed' : 'did not complete';
          console.log(`completeDebrief: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<ICompleteDebriefResponse>(
            'completeDebrief request',
          ),
        ),
      );
  }

  updateInstanceProgress(
    instanceUuid: string,
    learnState: IPdfLearnState | IVideoLearnState | IScormLearnState
  ): Observable<void> {
    if (!instanceUuid) {
      throw new Error('instanceUuid is required');
    }
    if (!learnState) {
      throw new Error('learnState is required');
    }

    const body = {
      instanceUuid: instanceUuid,
      progress: JSON.stringify(learnState),
    };
    return this.http
      .post<any>(`${this.backendUrl}/pathwayLearner/updateProgress`, body)
      .pipe(
        tap(() => {
          console.log(`updateInstanceProgress: updated`);
        }),
        catchError(
          this.errorHandlingService.handleError<void>(
            'updateInstanceProgress request'
          )
        )
      );
  }

  getAssessmentReadiness(enrollmentUuid: string): Observable<boolean> {
    if (!enrollmentUuid) {
      throw new Error('enrollmentUuid is required');
    }

    const params = new HttpParams().set('enrollmentUuid', enrollmentUuid);

    return this.http
      .get<boolean>(`${this.backendUrl}/pathwayLearner/getAssessmentReadiness`, { params })
      .pipe(
        tap((ready) => console.log(`getAssessmentReadiness: ${ready}`)),
        catchError(
          this.errorHandlingService.handleError<boolean>(
            'getAssessmentReadiness request'
          )
        )
      );
  }
}
